// Day-selection helpers for scoping a photo compile to a subset of capture
// days (#385). Client mirror of functions/shared/day_selection.py — the two
// must agree, or the count the UI promises won't match the video that lands.
//
// Three shapes, matching the wire contract the compile callable validates:
//
//   { mode: 'all' }
//   { mode: 'range', start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }   inclusive
//   { mode: 'days',  days: ['YYYY-MM-DD', ...] }
//
// Day keys are UTC (see photoUpload/dayBuckets.js). 'unknown' is the bucket
// for photos with no readable capture timestamp: selectable explicitly, but
// never inside a range, since it has no place on a calendar.

export const MODE_ALL = 'all';
export const MODE_RANGE = 'range';
export const MODE_DAYS = 'days';
export const UNKNOWN_DAY = 'unknown';

export const SELECTION_ALL = { mode: MODE_ALL };

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Coerce a persisted/unknown value into a usable selection.
 *
 * Anything unrecognised becomes "all photos" — the pre-#385 behaviour — so a
 * malformed project doc degrades to compiling everything rather than nothing.
 */
export function normalizeSelection(raw) {
  if (!raw || typeof raw !== 'object') return SELECTION_ALL;
  if (raw.mode === MODE_RANGE) {
    const { start, end } = raw;
    if (DAY_RE.test(start) && DAY_RE.test(end) && start <= end) {
      return { mode: MODE_RANGE, start, end };
    }
    return SELECTION_ALL;
  }
  if (raw.mode === MODE_DAYS) {
    const days = Array.isArray(raw.days)
      ? raw.days.filter((d) => d === UNKNOWN_DAY || DAY_RE.test(d))
      : [];
    return days.length ? { mode: MODE_DAYS, days: [...new Set(days)].sort() } : SELECTION_ALL;
  }
  return SELECTION_ALL;
}

/** Does a capture day fall inside this selection? Mirrors `day_matches`. */
export function daySelected(selection, dayKey) {
  const sel = selection || SELECTION_ALL;
  if (sel.mode === MODE_DAYS) return (sel.days || []).includes(dayKey);
  if (sel.mode === MODE_RANGE) {
    // 'unknown' sorts after every real date as a string, so a range must
    // exclude it explicitly rather than by comparison.
    if (dayKey === UNKNOWN_DAY) return false;
    return sel.start <= dayKey && dayKey <= sel.end;
  }
  return true;
}

/**
 * Apply a selection to the coverage rollup (`coverage.days`).
 *
 * Returns { photoCount, dayCount, days, missingDays } — the same shape the
 * preview callable returns, so the instant local estimate and the server's
 * authoritative answer are directly comparable.
 */
export function resolveSelection(selection, coverageDays) {
  const days = coverageDays && typeof coverageDays === 'object' ? coverageDays : {};
  const matched = [];
  let photoCount = 0;
  Object.keys(days).forEach((dayKey) => {
    const count = days[dayKey]?.photoCount;
    if (typeof count !== 'number' || count <= 0) return;
    if (daySelected(selection, dayKey)) {
      matched.push(dayKey);
      photoCount += count;
    }
  });
  const missingDays = selection?.mode === MODE_DAYS
    ? (selection.days || []).filter((d) => !matched.includes(d))
    : [];
  return { photoCount, dayCount: matched.length, days: matched.sort(), missingDays };
}

/** Stable equality, for skipping redundant preview calls. */
export function selectionsEqual(a, b) {
  const x = normalizeSelection(a);
  const y = normalizeSelection(b);
  if (x.mode !== y.mode) return false;
  if (x.mode === MODE_RANGE) return x.start === y.start && x.end === y.end;
  if (x.mode === MODE_DAYS) {
    return x.days.length === y.days.length && x.days.every((d, i) => d === y.days[i]);
  }
  return true;
}

/** Output duration in seconds — one frame per photo. */
export function estimateSeconds(photoCount, fps) {
  if (!fps || fps <= 0) return 0;
  return photoCount / fps;
}

/**
 * Build the wire selection for a set of chosen day keys.
 *
 * Collapses to `{mode:'all'}` when every available day is chosen, so the
 * common case persists the compact form and a *re*compile automatically
 * picks up days that did not exist when the choice was made.
 */
export function selectionFromDays(selectedDays, allDayKeys) {
  const chosen = [...selectedDays];
  if (!chosen.length) return { mode: MODE_DAYS, days: [] };
  if (allDayKeys && chosen.length === allDayKeys.length) return SELECTION_ALL;
  return { mode: MODE_DAYS, days: chosen.sort() };
}

/** The day keys a selection covers, given everything available. */
export function daysForSelection(selection, allDayKeys) {
  return (allDayKeys || []).filter((d) => daySelected(selection, d));
}
